import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { AlertTriangle, Crown, ExternalLink, Link2, MessageSquare, ShoppingBag, Package } from 'lucide-react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { currentLocale, i18n } from '@/lib/i18n'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { StageBadge } from '@/components/shared/stage-badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiError } from '@/lib/api-client'
import { sourceLabel } from '@/lib/data-sources'
import { formatVnd } from '@/lib/format-money'
import { cn } from '@/lib/utils'
import { useCdpEventStats, useEventDefinitions } from '@/hooks/use-cdp'
import { useContactEvotech, useContactOrders } from '@/hooks/use-evotech'
import { resolveImageUrl } from '@/hooks/use-products'
import { safeHttpUrl } from '@/lib/safe-url'
import {
  useContactFieldSources,
  useContactOverview,
  useContactSegments,
  useLinkedSources,
  useResolveFieldSource,
  type FieldKey,
  useContactTimeline,
  type RfmResult,
  type TimelineItem,
} from '@/hooks/use-contact-overview'
import { useUpdateContact, type ContactDetail } from '@/hooks/use-contacts'

export function age(birthday?: string | null): number | null {
  if (!birthday) return null
  const b = dayjs(birthday)
  return b.isValid() ? dayjs().diff(b, 'year') : null
}

function SectionCard({ title, action, children, className }: { title: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

function Row({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="break-words font-medium">{value || '—'}</span>
    </div>
  )
}

// ── Hàng hồ sơ có nguồn: giá trị đang dùng + nguồn + các giá trị khác từ nguồn khác ──
function SourcedRow({ contactId, field, label, fallback }: { contactId: string; field: FieldKey; label: string; fallback?: string | null }) {
  const { t } = useTranslation('custDetail')
  const { data } = useContactFieldSources(contactId)
  const resolve = useResolveFieldSource(contactId)
  const info = data?.fields[field]
  const value = info?.current ?? fallback
  const others = info?.others ?? []
  const run = (body: { source?: string; keep?: boolean }, done: string) =>
    resolve.mutate({ field, ...body }, { onSuccess: () => toast.success(done), onError: (e) => toast.error(apiError(e)) })
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <div className="min-w-0">
        <span className="break-words font-medium">{value || '—'}</span>
        {value && info?.source && (
          <span className="ml-2 text-[11px] text-muted-foreground" title={info.source.at ? t('sourced.updated', { time: dayjs(info.source.at).format('HH:mm DD/MM/YYYY') }) : t('sourced.legacy')}>
            · {info.source.label}
          </span>
        )}
        {others.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="ml-2 inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" /> {t('sourced.valuesCount', { count: others.length + 1 })}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 space-y-2 text-sm">
              <p className="text-xs text-muted-foreground">{t('sourced.conflictHelp', { label })}</p>
              <div className="rounded-md border bg-muted/40 p-2">
                <p className="text-[11px] text-muted-foreground">{t('sourced.inUse', { source: info?.source?.label ?? '—' })}</p>
                <p className="break-words font-medium">{value}</p>
              </div>
              {others.map((o) => (
                <div key={o.source} className="flex items-start gap-2 rounded-md border p-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-muted-foreground">{o.label} · {dayjs(o.at).format('DD/MM/YYYY')}</p>
                    <p className="break-words font-medium">{o.value}</p>
                  </div>
                  <Button size="sm" variant="outline" disabled={resolve.isPending} onClick={() => run({ source: o.source }, t('sourced.usedFrom', { source: o.label }))}>
                    {t('sourced.useThis')}
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="w-full" disabled={resolve.isPending} onClick={() => run({ keep: true }, t('sourced.kept'))}>
                {t('sourced.keepCurrent')}
              </Button>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  )
}

// ── Hồ sơ khách hàng ─────────────────────────────────────────────────
/**
 * Thẻ hồ sơ. "Chỉnh sửa" mở ô điền NGAY TRONG thẻ (không hộp thoại): họ tên, SĐT, email, ngày sinh, nghề nghiệp, nguồn khách.
 * Giai đoạn đổi ở thanh giai đoạn (có trường bổ sung của bước), phụ trách ở menu Hành động — nên không nằm trong form này.
 */
export function ProfileCard({ contact, editing = false, onEdit, onClose }: { contact: ContactDetail; editing?: boolean; onEdit: () => void; onClose: () => void }) {
  const { t } = useTranslation('custDetail')
  const bd = contact.birthday
  const a = age(bd)
  const update = useUpdateContact()
  const init = () => ({
    fullName: contact.fullName ?? '',
    phone: contact.phone ?? '',
    email: contact.email ?? '',
    birthday: bd ? String(bd).slice(0, 10) : '',
    jobTitle: contact.jobTitle ?? '',
    source: contact.source ?? '',
  })
  const [form, setForm] = useState(init)
  // Mỗi lần mở chế độ sửa: nạp lại giá trị hiện tại của khách.
  useEffect(() => {
    if (editing) setForm(init())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, contact.id])
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))
  const save = () =>
    update.mutate(
      { id: contact.id, data: { ...form, birthday: form.birthday || null } },
      {
        onSuccess: () => {
          toast.success(t('profile.updated'))
          onClose()
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )

  return (
    <SectionCard
      title={t('profile.title')}
      action={
        editing ? (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7" onClick={onClose} disabled={update.isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button size="sm" className="h-7" onClick={save} disabled={update.isPending}>
              {update.isPending ? t('profile.saving') : t('common:actions.save')}
            </Button>
          </div>
        ) : (
          <Button variant="link" size="sm" className="h-auto p-0" onClick={onEdit}>
            {t('profile.edit')}
          </Button>
        )
      }
    >
      {editing ? (
        <form
          className="grid gap-2.5 py-1"
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
        >
          {([
            ['fullName', t('profile.fullName'), 'text'],
            ['phone', t('profile.phone'), 'tel'],
            ['email', 'Email', 'email'],
            ['birthday', t('profile.birthday'), 'date'],
            ['jobTitle', t('profile.job'), 'text'],
            ['source', t('profile.source'), 'text'],
          ] as const).map(([k, label, type]) => (
            <label key={k} className="grid grid-cols-[110px_1fr] items-center gap-2 text-sm">
              <span className="text-muted-foreground">{label}</span>
              <Input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} autoFocus={k === 'fullName'} className="h-9" />
            </label>
          ))}
          <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
        </form>
      ) : (
        <>
          <SourcedRow contactId={contact.id} field="fullName" label={t('profile.fullName')} fallback={contact.crmName || contact.fullName} />
          <SourcedRow contactId={contact.id} field="phone" label={t('profile.phone')} fallback={contact.phone} />
          <SourcedRow contactId={contact.id} field="email" label="Email" fallback={contact.email} />
          <Row label={t('profile.birthday')} value={bd ? `${dayjs(bd).format('DD/MM/YYYY')}${a != null ? ` (${t('profile.age', { count: a })})` : ''}` : null} />
          <SourcedRow contactId={contact.id} field="jobTitle" label={t('profile.job')} fallback={contact.jobTitle} />
          <Row label={t('profile.source')} value={contact.source} />
        </>
      )}
      <Row label={t('profile.stage')} value={<StageBadge stage={contact.lifecycleStage} />} />
      <Row label={t('profile.assignee')} value={contact.assignedUser?.fullName} />
      <Row label={t('profile.createdAt')} value={dayjs(contact.createdAt).format('DD/MM/YYYY')} />
    </SectionCard>
  )
}

// ── Thành viên & phân khúc ───────────────────────────────────────────
export function MemberSegmentsCard({ contactId }: { contactId: string }) {
  const { t } = useTranslation('custDetail')
  const member = useContactEvotech(contactId)
  const seg = useContactSegments(contactId)
  const m = member.data && member.data.configured && member.data.linked ? member.data.member : null
  const segments = seg.data?.segments ?? []
  const pct =
    m && m.nextLevel && m.remainingSpend != null && m.totalSpent != null && m.totalSpent + m.remainingSpend > 0
      ? Math.min(100, Math.round((m.totalSpent / (m.totalSpent + m.remainingSpend)) * 100))
      : null
  return (
    <SectionCard title={t('member.title')}>
      {m && (
        <div className="mb-4 rounded-lg border bg-muted/30 p-3">
          <div className="flex items-center gap-3">
            <Crown className="h-8 w-8 text-amber-500" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{m.level || t('member.fallbackLevel')}</p>
              <p className="text-xs text-muted-foreground">
                {m.point != null ? t('member.points', { points: new Intl.NumberFormat(currentLocale()).format(m.point) }) : '—'}
                {m.nextLevel && m.remainingSpend != null ? t('member.remaining', { amount: formatVnd(m.remainingSpend), level: m.nextLevel }) : ''}
              </p>
            </div>
          </div>
          {pct != null && (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
      )}
      <p className="mb-2 text-sm font-medium">{t('member.currentSegments')}</p>
      {segments.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <Trans t={t} i18nKey="member.noSegments" components={{ l: <Link to="/customers/segments" className="text-primary hover:underline" /> }} />
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {segments.map((s) => (
            <Badge key={s.id} variant="secondary" className="px-2.5 py-1 text-xs" title={t('member.entered', { date: dayjs(s.enteredAt).format('DD/MM/YYYY') })}>
              {s.name}
            </Badge>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

// ── Chỉ số RFM ───────────────────────────────────────────────────────
export function RfmCard({ contactId, source }: { contactId: string; source?: string }) {
  const { t } = useTranslation('custDetail')
  const rfm: RfmResult | null | undefined = useContactOverview(contactId, source).data?.rfm
  const tiles = rfm
    ? [
        { label: 'Recency', score: rfm.r, note: rfm.recencyDays == null ? '—' : t('rfm.daysAgo', { count: rfm.recencyDays }) },
        { label: 'Frequency', score: rfm.f, note: t('rfm.orders', { count: rfm.frequency }) },
        { label: 'Monetary', score: rfm.m, note: formatVnd(rfm.monetary) },
      ]
    : []
  return (
    <SectionCard title={t('rfm.title')}>
      {!rfm ? (
        <p className="text-sm text-muted-foreground">{t('rfm.empty')}</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            {tiles.map((tile) => (
              <div key={tile.label} className="rounded-lg bg-muted/40 p-3 text-center">
                <p className="text-xs text-muted-foreground">{tile.label}</p>
                <p className="text-3xl font-bold tabular-nums text-primary">{tile.score}</p>
                <p className="truncate text-xs text-muted-foreground" title={tile.note}>{tile.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">{t('rfm.note')}</p>
        </>
      )}
    </SectionCard>
  )
}

// ── Tổng quan hành vi + biểu đồ ──────────────────────────────────────
export function BehaviorSummaryCard({ contactId }: { contactId: string }) {
  const { t } = useTranslation('custDetail')
  const stats = useCdpEventStats(30, contactId)
  const defs = useEventDefinitions()
  const names = useMemo(() => new Map((defs.data ?? []).map((d) => [d.eventName, d.displayName])), [defs.data])
  const top = (stats.data?.byEventName ?? []).slice(0, 3)
  return (
    <SectionCard title={t('behavior.title')} action={<span className="text-xs text-muted-foreground">{t('behavior.last30')}</span>}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">{t('behavior.totalEvents')}</p>
          <p className="text-2xl font-bold tabular-nums">{stats.data?.totalEvents ?? 0}</p>
        </div>
        {top.map((ev) => (
          <div key={ev.name} className="rounded-lg border p-3">
            <p className="truncate text-xs text-muted-foreground" title={names.get(ev.name) ?? ev.name}>{names.get(ev.name) ?? ev.name}</p>
            <p className="text-2xl font-bold tabular-nums">{ev.count}</p>
          </div>
        ))}
      </div>
      {top.length === 0 && <p className="mt-2 text-sm text-muted-foreground">{t('behavior.empty')}</p>}
    </SectionCard>
  )
}

const MONTH_LABEL = (k: string) => i18n.t('custDetail:trend.monthShort', { n: Number(k.slice(5)) })

export function TrendChartCard({ contactId, source }: { contactId: string; source?: string }) {
  const { t } = useTranslation('custDetail')
  const { data } = useContactOverview(contactId, source)
  const rows = (data?.trend ?? []).map((pt) => ({ ...pt, label: MONTH_LABEL(pt.month) }))
  const empty = rows.every((r) => !r.revenue && !r.orders && !r.events)
  return (
    <SectionCard title={t('trend.title')}>
      {empty ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('trend.empty')}</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={rows} margin={{ left: -8, right: 8, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
            <YAxis yAxisId="l" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} tickFormatter={(v) => (v >= 1e6 ? `${v / 1e6}${t('trend.unitMillion')}` : v >= 1e3 ? `${v / 1e3}${t('trend.unitThousand')}` : String(v))} />
            <YAxis yAxisId="r" orientation="right" tickLine={false} axisLine={false} allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: number, n: string) => (n === t('trend.revenue') ? formatVnd(v) : v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="l" dataKey="revenue" name={t('trend.revenue')} fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            <Line yAxisId="r" type="monotone" dataKey="orders" name={t('trend.orders')} stroke="hsl(var(--success))" strokeWidth={2} dot={false} />
            <Line yAxisId="r" type="monotone" dataKey="events" name={t('trend.events')} stroke="#8b5cf6" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </SectionCard>
  )
}

// ── Sản phẩm hay mua ─────────────────────────────────────────────────
export function TopProductsCard({ contactId, source }: { contactId: string; source?: string }) {
  const { t } = useTranslation('custDetail')
  const { data } = useContactOverview(contactId, source)
  const items = data?.topProducts ?? []
  return (
    <SectionCard title={t('products.title')}>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('products.empty')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((p) => (
            <div key={p.sku ?? p.name} className="flex min-w-0 flex-col overflow-hidden rounded-lg border">
              <ProductImage src={p.image} className="aspect-square w-full border-b" />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5 p-2.5">
                <p className="line-clamp-2 text-sm font-medium leading-snug" title={p.name}>{p.name}</p>
                {p.sku && <p className="truncate font-mono text-[11px] text-muted-foreground" title={p.sku}>{p.sku}</p>}
                <p className="mt-auto pt-1 text-xs text-muted-foreground">{t('products.bought', { quantity: p.quantity, orders: p.orders })}</p>
                {safeHttpUrl(p.link) && (
                  <a href={safeHttpUrl(p.link)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <ExternalLink className="h-3 w-3" /> {t('products.view')}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

/** Ảnh sản phẩm (đơn / sản phẩm hay mua); không có hoặc lỗi tải → biểu tượng hộp. */
function ProductImage({ src, className }: { src?: string | null; className?: string }) {
  const [broken, setBroken] = useState(false)
  const url = resolveImageUrl(src)
  return (
    <span className={cn('flex items-center justify-center overflow-hidden bg-muted/40', className)}>
      {url && !broken ? (
        <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" onError={() => setBroken(true)} />
      ) : (
        <Package className="h-5 w-5 text-muted-foreground/60" />
      )}
    </span>
  )
}

// ── Đơn hàng gần nhất ────────────────────────────────────────────────
export function RecentOrdersCard({ contactId, onAll }: { contactId: string; onAll: () => void }) {
  const { t } = useTranslation('custDetail')
  const { data } = useContactOrders(contactId)
  const orders = (data?.orders ?? []).slice(0, 3)
  return (
    <SectionCard
      title={t('orders.title')}
      action={<Button variant="link" size="sm" className="h-auto p-0" onClick={onAll}>{t('common:actions.viewAll')}</Button>}
    >
      {orders.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground"><ShoppingBag className="h-4 w-4" /> {t('orders.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="pb-2 font-medium">{t('orders.code')}</th>
                <th className="pb-2 font-medium">{t('orders.placedAt')}</th>
                <th className="pb-2 font-medium">{t('orders.product')}</th>
                <th className="pb-2 text-right font-medium">{t('orders.total')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const firstItem = o.items?.[0]
                const first = firstItem ? firstItem.productName || firstItem.name : undefined
                const more = (o.items?.length ?? 0) - 1
                return (
                  <tr key={o.id} className="border-t">
                    <td className="py-2 font-mono text-xs text-primary">{o.externalId}</td>
                    <td className="py-2">{o.placedAt ? dayjs(o.placedAt).format('DD/MM/YYYY') : '—'}</td>
                    <td className="max-w-[260px] py-2" title={first}>
                      <span className="flex min-w-0 items-center gap-2">
                        <ProductImage src={firstItem?.image} className="h-8 w-8 shrink-0 rounded-md border" />
                        <span className="truncate">{first ?? '—'}{more > 0 ? t('orders.more', { count: more }) : ''}</span>
                      </span>
                    </td>
                    <td className="py-2 text-right tabular-nums">{formatVnd(o.total)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  )
}

// ── Kênh tương tác ───────────────────────────────────────────────────
export function ChannelsCard({ contact }: { contact: ContactDetail }) {
  const { t } = useTranslation('custDetail')
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; count: number; last: string | null }>()
    for (const c of contact.conversations ?? []) {
      const name = c.channelAccount?.displayName || t('channels.other')
      const g = map.get(name) ?? { name, count: 0, last: null }
      g.count += 1
      if (c.lastMessageAt && (!g.last || c.lastMessageAt > g.last)) g.last = c.lastMessageAt
      map.set(name, g)
    }
    return [...map.values()]
  }, [contact.conversations, t])
  return (
    <SectionCard title={t('channels.title')}>
      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('channels.empty')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {groups.map((g) => (
            <div key={g.name} className="rounded-lg border p-3 text-center">
              <MessageSquare className="mx-auto mb-1 h-5 w-5 text-primary" />
              <p className="truncate text-sm font-medium" title={g.name}>{g.name}</p>
              <p className="text-xs text-muted-foreground">{t('channels.conversations', { count: g.count })}</p>
              <p className="text-[11px] text-muted-foreground">{g.last ? dayjs(g.last).format('DD/MM HH:mm') : '—'}</p>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

// ── Dòng thời gian ───────────────────────────────────────────────────
const TYPE_LABEL: Record<TimelineItem['type'], string> = {
  get message() { return i18n.t('custDetail:timeline.type.message') },
  get note() { return i18n.t('custDetail:timeline.type.note') },
  get appointment() { return i18n.t('custDetail:timeline.type.appointment') },
  get event() { return i18n.t('custDetail:timeline.type.event') },
  get lifecycle() { return i18n.t('custDetail:timeline.type.lifecycle') },
}

function describe(it: TimelineItem, eventNames: Map<string, string>, stageLabel: (s?: string | null) => string): { title: string; desc?: string } {
  const d = it.data ?? {}
  switch (it.type) {
    case 'message':
      return { title: d.senderType === 'contact' ? i18n.t('custDetail:timeline.customerMessage') : i18n.t('custDetail:timeline.sentMessage'), desc: typeof d.content === 'string' ? d.content.slice(0, 120) : undefined }
    case 'note':
      return { title: d.authorName ? i18n.t('custDetail:timeline.noteBy', { author: d.authorName }) : i18n.t('custDetail:timeline.note'), desc: typeof d.content === 'string' ? d.content.slice(0, 120) : undefined }
    case 'appointment':
      return { title: d.title ? i18n.t('custDetail:timeline.appointmentTitled', { title: d.title }) : i18n.t('custDetail:timeline.appointment'), desc: d.assignedName ? i18n.t('custDetail:timeline.assignedTo', { name: d.assignedName }) : undefined }
    case 'lifecycle':
      return { title: i18n.t('custDetail:timeline.stageChange', { from: stageLabel(d.fromStage), to: stageLabel(d.toStage) }), desc: d.reason || undefined }
    default:
      return { title: eventNames.get(d.eventName) ?? d.eventName ?? i18n.t('custDetail:timeline.event') }
  }
}

export function Timeline({
  contactId,
  limit,
  types,
  stageLabel,
  empty,
}: {
  contactId: string
  limit: number
  types?: TimelineItem['type'][]
  stageLabel: (s?: string | null) => string
  empty?: string
}) {
  const { t } = useTranslation('custDetail')
  const { data, isLoading } = useContactTimeline(contactId, limit > 50 ? 100 : 50)
  const defs = useEventDefinitions()
  const names = useMemo(() => new Map((defs.data ?? []).map((d) => [d.eventName, d.displayName])), [defs.data])
  const items = (data?.items ?? []).filter((i) => !types || types.includes(i.type)).slice(0, limit)
  if (isLoading) return <div className="h-16 animate-pulse rounded-md bg-muted/40" />
  if (items.length === 0) return <p className="text-sm text-muted-foreground">{empty ?? t('timeline.empty')}</p>
  return (
    <ul className="space-y-3">
      {items.map((it, i) => {
        const d = describe(it, names, stageLabel)
        return (
          <li key={`${it.type}-${it.timestamp}-${i}`} className="flex gap-3 text-sm">
            <span className="w-24 shrink-0 text-xs text-muted-foreground">{dayjs(it.timestamp).format('DD/MM HH:mm')}</span>
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                <Badge variant="outline" className="mr-2 px-1.5 py-0 text-[10px]">{TYPE_LABEL[it.type]}</Badge>
                {d.title}
              </p>
              {d.desc && <p className="truncate text-xs text-muted-foreground">{d.desc}</p>}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export function RecentActivityCard({ contactId, stageLabel, onAll }: { contactId: string; stageLabel: (s?: string | null) => string; onAll: () => void }) {
  const { t } = useTranslation('custDetail')
  return (
    <SectionCard title={t('timeline.recent')} action={<Button variant="link" size="sm" className="h-auto p-0" onClick={onAll}>{t('common:actions.viewAll')}</Button>}>
      <Timeline contactId={contactId} limit={6} stageLabel={stageLabel} />
    </SectionCard>
  )
}

export { SectionCard }

// ── Lọc theo nguồn đơn hàng (Mua hàng) ───────────────────────────────
export function OrderSourceFilter({ contactId, value, onChange }: { contactId: string; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('custDetail')
  const { data } = useLinkedSources(contactId)
  const sources = data?.orders ?? []
  if (sources.length < 2) return null
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('orderSource.aria')}>
      <span className="text-xs text-muted-foreground">{t('orderSource.label')}</span>
      {[{ source: 'all', count: sources.reduce((n, x) => n + x.count, 0) }, ...sources].map((x) => (
        <button
          key={x.source}
          type="button"
          aria-pressed={value === x.source}
          onClick={() => onChange(x.source)}
          className={`rounded-full border px-3 py-1 text-xs ${value === x.source ? 'border-primary bg-primary/10 font-semibold text-primary' : 'text-muted-foreground hover:bg-muted'}`}
        >
          {x.source === 'all' ? t('orderSource.all') : sourceLabel(x.source)} <span className="tabular-nums opacity-70">{x.count}</span>
        </button>
      ))}
    </div>
  )
}

// ── Nguồn dữ liệu liên kết ───────────────────────────────────────────
export function LinkedSourcesCard({ contactId }: { contactId: string }) {
  const { t } = useTranslation('custDetail')
  const { data, isLoading } = useLinkedSources(contactId)
  const ago = (d: string | null) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—')
  return (
    <SectionCard title={<span className="flex items-center gap-2"><Link2 className="h-4 w-4" /> {t('linked.title')}</span>} action={<Link to="/integrations" className="text-xs text-primary hover:underline">{t('linked.manage')}</Link>}>
      {isLoading ? (
        <div className="h-16 animate-pulse rounded-md bg-muted/40" />
      ) : !data ? null : (
        <div className="space-y-4 text-sm">
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t('linked.identities')}</p>
            {/* Chỉ nền tảng tổ chức đã kết nối (hoặc khách đã có định danh) — máy chủ lọc sẵn. */}
            {data.identities.length === 0 ? (
              <p className="text-xs text-muted-foreground"><Trans t={t} i18nKey="linked.noIdentities" components={{ l: <Link to="/integrations" className="text-primary hover:underline" /> }} /></p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {data.identities.map((r) => (
                  <span key={r.key} className={`rounded-md border px-2 py-0.5 text-xs ${r.linked ? 'border-primary/40 bg-primary/10 text-primary' : 'text-muted-foreground'}`} title={r.hint}>
                    {r.label}: {r.linked ? (r.hint ?? t('linked.linked')) : t('linked.none')}
                  </span>
                ))}
              </div>
            )}
          </div>
          <SourceTable title={t('linked.channels')} empty={t('linked.noConversations')} rows={data.channels.map((c) => ({ key: c.id, name: `${c.platform}${c.name ? ` · ${c.name}` : ''}`, detail: t('linked.conversations', { count: c.conversations }), at: ago(c.lastAt) }))} />
          <SourceTable title={t('linked.ordersBySource')} empty={t('linked.noOrders')} rows={data.orders.map((o) => ({ key: o.source, name: sourceLabel(o.source), detail: t('linked.ordersDetail', { count: o.count, total: formatVnd(o.total) }), at: ago(o.lastAt) }))} />
          <SourceTable title={t('linked.eventsBySource')} empty={t('linked.noEvents')} rows={data.events.map((e) => ({ key: e.source, name: sourceLabel(e.source), detail: t('linked.eventsDetail', { count: e.count }), at: ago(e.lastAt) }))} />
        </div>
      )}
    </SectionCard>
  )
}

function SourceTable({ title, rows, empty }: { title: string; rows: { key: string; name: string; detail: string; at: string }[]; empty: string }) {
  const { t } = useTranslation('custDetail')
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">{title}</p>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {rows.map((r) => (
            <li key={r.key} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2">
              <span className="min-w-[120px] font-medium">{r.name}</span>
              <span className="text-muted-foreground">{r.detail}</span>
              <span className="ml-auto text-xs text-muted-foreground">{t('linked.updatedAt', { at: r.at })}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
